import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import type { ManagedOrganization } from "../types";
import { gql, useQuery } from "@apollo/client";

const MY_MANAGED_ORGS = gql`
  query MyManagedOrganizations {
    myManagedOrganizations {
      _id
      name
      slug
      type
      picture
      memberCount
    }
  }
`;

interface OrgContextValue {
  /** Currently selected organization ID */
  currentOrgId: string | null;
  /** Currently selected organization details (includes slug) */
  currentOrg: ManagedOrganization | null;
  /** All organizations the user can manage */
  managedOrgs: ManagedOrganization[];
  /** Switch to a different organization by ID (called by OrgSlugGuard) */
  setOrganization: (orgId: string | null) => void;
  /** Whether the managed orgs are loading */
  isLoading: boolean;
}

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);

  const { data, loading } = useQuery<{
    myManagedOrganizations: ManagedOrganization[];
  }>(MY_MANAGED_ORGS, {
    skip: !isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const managedOrgs = data?.myManagedOrganizations ?? [];

  // No auto-select — URL slug (via OrgSlugGuard) is the source of truth.
  // OrgSlugGuard calls setOrganization() when /:orgSlug matches a managed org.

  const currentOrg = useMemo(
    () => managedOrgs.find((o) => o._id === currentOrgId) ?? null,
    [managedOrgs, currentOrgId]
  );

  const setOrganization = useCallback((orgId: string | null) => {
    setCurrentOrgId(orgId);
  }, []);

  const value = useMemo<OrgContextValue>(
    () => ({
      currentOrgId,
      currentOrg,
      managedOrgs,
      setOrganization,
      isLoading: loading,
    }),
    [currentOrgId, currentOrg, managedOrgs, setOrganization, loading]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrgContext() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrgContext must be used within OrgProvider");
  return ctx;
}
